// super-excalidraw: quick-edit dialog for a single library item.
// Opens from the library kebab when exactly one item is selected. Sets
// name + description without going through the publish flow. PATCHes
// /api/library/<id> directly (intentionally inline so this file stays
// inside the upstream package and doesn't need a cross-package import).

import React, { useState } from "react";

import { Dialog } from "./Dialog";
import { FilledButton } from "./FilledButton";

import type { LibraryItem } from "../types";

import "./LibraryItemEditPopover.scss";

export type LibraryItemEditPopoverProps = {
  item: LibraryItem;
  onClose: () => void;
  /** Called with the saved values so callers can update local state to
   *  match the server. Description isn't part of upstream's LibraryItem
   *  type but rides along on the object. */
  onSaved: (next: { name: string; description: string }) => void;
};

export const LibraryItemEditPopover: React.FC<LibraryItemEditPopoverProps> = ({
  item,
  onClose,
  onSaved,
}) => {
  const initialDescription = ((item as any).description as string) ?? "";
  const [name, setName] = useState(item.name ?? "");
  const [description, setDescription] = useState(initialDescription);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSave = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await fetch(
        `/api/library/${encodeURIComponent(item.id)}`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ name, description }),
        },
      );
      if (!r.ok) {
        throw new Error(`PATCH ${r.status}`);
      }
      onSaved({ name, description });
      onClose();
    } catch (e: any) {
      setError(e?.message ?? String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      onCloseRequest={onClose}
      title="Edit library item"
      size="small"
      className="library-item-edit"
    >
      <div className="library-item-edit__body">
        <label className="library-item-edit__field">
          <span className="library-item-edit__label">Name</span>
          <input
            autoFocus
            className="library-item-edit__input"
            placeholder="Untitled"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </label>
        <label className="library-item-edit__field">
          <span className="library-item-edit__label">
            Description
            <span className="library-item-edit__hint">
              (so the AI knows when to use it)
            </span>
          </span>
          <textarea
            className="library-item-edit__textarea"
            placeholder="Lambda function — orange square with λ"
            rows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </label>
        {error && (
          <div className="library-item-edit__error">Error: {error}</div>
        )}
        <div className="library-item-edit__actions">
          <FilledButton
            label="Cancel"
            variant="outlined"
            color="muted"
            onClick={onClose}
            size="medium"
          />
          <FilledButton
            label={busy ? "Saving…" : "Save"}
            color="primary"
            size="medium"
            onClick={onSave}
          />
        </div>
      </div>
    </Dialog>
  );
};
